import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PrintStoreImages } from "../galleryImages";
import { useCart } from "../context/CartContext";
import "./PrintDetail.css";

export default function PrintDetail() {
  const { id } = useParams();

  const { addToCart } = useCart();

  const [size, setSize] = useState(null);
  const [paper, setPaper] = useState(null);
  const [frame, setFrame] = useState("Unframed");

  const [flyer, setFlyer] = useState(null);

  const image = PrintStoreImages.find((item) => String(item.id) === String(id));

  if (!image) {
    return (
      <main className="print-detail-page">
        <p>Print not found.</p>

        <Link to="/prints">BACK TO PRINTS</Link>
      </main>
    );
  }

  const print = image.print;

  const selectedSize = size || Object.keys(print.sizes)[0];

  const selectedPaper = paper || Object.keys(print.papers)[0];

  const printPrice = print.sizes[selectedSize] || 0;

  const paperPrice = print.papers[selectedPaper] || 0;

  const framePrice =
    typeof print.frames[frame] === "object"
      ? print.frames[frame][selectedSize] || 0
      : print.frames[frame] || 0;

  const totalPrice = printPrice + paperPrice + framePrice;

  /* =======================================================
     ADD TO CART
     ======================================================= */

  const handleAddToCart = () => {
    const button = document.querySelector(".print-add-button");

    /*
      Capture where the animation starts.
    */

    let startX = window.innerWidth / 2;
    let startY = window.innerHeight / 2;

    if (button) {
      const buttonRect = button.getBoundingClientRect();

      startX = buttonRect.right - 30;

      startY = buttonRect.top + buttonRect.height / 2;
    }

    /* -------------------------------------------------------
       Add item to cart
       ------------------------------------------------------- */

    const cartItem = {
      printId: image.id,
      title: image.title,
      photo: image.photo,
      size: selectedSize,
      paper: selectedPaper,
      frame,
      price: totalPrice,
    };

    addToCart(cartItem);

    /*
      The cart may not exist yet if this is the first item.

      Wait for React to render the navbar cart before
      calculating its position.
    */

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const cartTarget = document.getElementById("navbar-cart-target");

        if (!cartTarget) {
          return;
        }

        const cartRect = cartTarget.getBoundingClientRect();

        const targetX = cartRect.left + cartRect.width / 2;

        const targetY = cartRect.top + cartRect.height / 2;

        /*
          Store both coordinates.

          Framer Motion will then animate directly
          between them.
        */

        setFlyer({
          startX,
          startY,
          targetX,
          targetY,
        });

        /*
          Remove flyer after animation.
        */

        setTimeout(() => {
          setFlyer(null);
        }, 800);
      });
    });
  };

  return (
    <main className="print-detail-page">
      {/* ==================================================
          BACK NAVIGATION
          ================================================== */}

      <div className="print-detail-nav">
        <Link to="/prints">← PRINTS</Link>
      </div>

      {/* ==================================================
          MAIN LAYOUT
          ================================================== */}

      <section className="print-detail-layout">
        {/* ==================================================
            IMAGE
            ================================================== */}

        <div className="print-detail-image">
          <img
            src={image.photo}
            alt={image.title || "Photograph"}
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>

        {/* ==================================================
            SIDEBAR
            ================================================== */}

        <aside className="print-detail-sidebar">
          {/* ------------------------------------------------
              TITLE
              ------------------------------------------------ */}

          <div className="print-detail-heading">
            <h1>{image.title || "Untitled"}</h1>
          </div>

          {/* ------------------------------------------------
              PRICE
              ------------------------------------------------ */}

          <div className="print-detail-price">
            ${totalPrice.toLocaleString()}
          </div>

          {/* =================================================
              SIZE
              ================================================= */}

          <div className="print-selector">
            <div className="selector-label">SIZE</div>

            {Object.entries(print.sizes).map(([option, optionPrice]) => (
              <button
                key={option}
                className={selectedSize === option ? "active" : ""}
                onClick={() => setSize(option)}
              >
                <span>{option}</span>

                <span>
                  ${optionPrice.toLocaleString()}
                  {selectedSize === option && " ✓"}
                </span>
              </button>
            ))}
          </div>

          {/* =================================================
              PAPER
              ================================================= */}

          <div className="print-selector">
            <div className="selector-label">PAPER</div>

            {Object.entries(print.papers).map(([option, optionPrice]) => (
              <button
                key={option}
                className={selectedPaper === option ? "active" : ""}
                onClick={() => setPaper(option)}
              >
                <span>{option}</span>

                <span>
                  {optionPrice === 0
                    ? "Included"
                    : `+$${optionPrice.toLocaleString()}`}

                  {selectedPaper === option && " ✓"}
                </span>
              </button>
            ))}
          </div>

          {/* =================================================
              FRAME
              ================================================= */}

          <div className="print-selector">
            <div className="selector-label">FRAME</div>

            {Object.entries(print.frames).map(([option, optionValue]) => {
              const optionPrice =
                typeof optionValue === "object"
                  ? optionValue[selectedSize] || 0
                  : optionValue;

              return (
                <button
                  key={option}
                  className={frame === option ? "active" : ""}
                  onClick={() => setFrame(option)}
                >
                  <span>{option}</span>

                  <span>
                    {optionPrice === 0
                      ? "Included"
                      : `+$${optionPrice.toLocaleString()}`}

                    {frame === option && " ✓"}
                  </span>
                </button>
              );
            })}
          </div>

          {/* =================================================
              ADD TO CART
              ================================================= */}

          <div className="print-add-wrapper">
            <button className="print-add-button" onClick={handleAddToCart}>
              ADD TO CART — ${totalPrice.toLocaleString()}
            </button>

            {/* ==============================================
                FLYING CART ITEM
                ============================================== */}

            <AnimatePresence>
              {flyer && (
                <motion.div
                  className="cart-flyer"
                  initial={{
                    left: flyer.startX,
                    top: flyer.startY,
                    opacity: 0,
                    scale: 0.4,
                  }}
                  animate={{
                    left: flyer.targetX,
                    top: flyer.targetY,

                    opacity: [0, 1, 1, 0],

                    scale: [0.4, 1, 0.9, 0.3],
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  transition={{
                    duration: 0.75,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <span>1</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* =================================================
              NOTES
              ================================================= */}

          <div className="print-detail-notes">
            <div>
              <span>EDITION</span>

              <p>{print.edition}</p>
            </div>

            <div>
              <span>PRODUCTION</span>

              <p>{print.production}</p>
            </div>

            <div>
              <span>SHIPPING</span>

              <p>Ships within 2–3 weeks</p>
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
}
